import MainBlock from "@container/MainBlock";
import MainContainer from "@container/MainContainer";
import WrapperBlock from "@container/WrapperBlock";
import Heading from "@elements/Typography/Heading";
import Audio from "@src/app/components/Audio/Audio";
import PodcastCard from "@src/app/components/Card/PodcastCard";
import SideBlock from "@src/app/components/Container/SideBlock";
import fetchTMApi from "@src/app/components/helper/fetchTMApi";
import LoadMore from "@src/app/hooks/useLoadMore/LoadMore";
import useSwrApi from "@src/app/hooks/useSwrApi";
import { type PodcastProcessedResponse } from "@transformer/usePodcastResponse";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import PodcastMetaData from "./PodcastMetaData";

export default function Podcast() {
  const { data } = useSwrApi<PodcastProcessedResponse[]>("podcasts");
  const { level1: id } = useParams();
  const [podcasts, setPodcasts] = useState<PodcastProcessedResponse[]>();
  const [size, setSize] = useState(20);
  const [hasMore, setHasMoreStories] = useState(true);
  const [playing, setPlaying] = useState<
    PodcastProcessedResponse | undefined
  >();

  useEffect(() => {
    const fetchData = async () => {
      if (!data || data.length === 0) return;
      let nowPlaying = false;
      try {
        const podcast: PodcastProcessedResponse[] = id
          ? await fetchTMApi<PodcastProcessedResponse[]>(
              `podcasts/${id}`,
              "GET",
            )
          : data;

        setPlaying(podcast[0]);
        nowPlaying = id ? false : true;
      } catch (error) {
        nowPlaying = true;
        setPlaying(data[0]);
      }

      const updatedPodcasts = data.map((podcast, index) =>
        index === 0
          ? { ...podcast, nowPlaying: nowPlaying ? true : false }
          : podcast,
      );
      setPodcasts(updatedPodcasts);
    };

    void fetchData();
  }, [id, data]);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setPodcasts(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setPodcasts],
  );

  const handleOnClickThumbnail = useCallback(
    (props: PodcastProcessedResponse) => {
      // eslint-disable-next-line react/prop-types
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },
    [handleNowPlaying, setPlaying],
  );

  const handleLoadMorePodcasts = async () => {
    const morePodcasts = await fetchTMApi<PodcastProcessedResponse[]>(
      `podcasts?size=${size}`,
      "GET",
    );
    if (
      morePodcasts.length === 0 ||
      morePodcasts.every((podcast) =>
        podcasts?.some((p) => p.id === podcast.id),
      )
    ) {
      setHasMoreStories(false);
    }
    setPodcasts((prevPodcast) => {
      const combinedPodcasts = [...(prevPodcast || []), ...morePodcasts];

      const uniquePodcastsMap = new Map();
      combinedPodcasts.forEach((podcast) =>
        uniquePodcastsMap.set(podcast.id, podcast),
      );
      const uniquePodcasts = Array.from(uniquePodcastsMap.values());
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return uniquePodcasts;
    });

    setSize((prevValue) => prevValue + 10);

    // This avoids GTM push.
    return 0;
  };

  const sectionName = "podcasts";
  const { metaTags } = PodcastMetaData(sectionName);

  return (
    <MainContainer metaTagsProps={metaTags} sectionName={sectionName}>
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock
          showTitle={false}
          pageName={sectionName}
          showBreadcrumbs={true}
        >
          {playing && <Audio {...playing} />}
          <Heading className="text-3xl font-bold font-primary">
            All Episodes
          </Heading>
          <div>
            {podcasts &&
              Array.isArray(podcasts) &&
              podcasts.map((podcast) => (
                <button
                  key={podcast.id}
                  onClick={() => {
                    handleOnClickThumbnail(podcast);
                  }}
                >
                  <PodcastCard
                    fullDate={podcast.dateDuration}
                    imageField={{ url: podcast.thumbnail }}
                    title={podcast.name}
                    paragraph={podcast.description}
                    linkTo=""
                    displayWidth={0}
                    displayHeight={0}
                    elements={{ hasDate: true }}
                  />
                </button>
              ))}
            <div className="flex w-full justify-start mb-xl">
              <LoadMore
                rootClassName="my-4 w-full"
                onLoadMore={handleLoadMorePodcasts}
                hasMore={hasMore}
                loadText="Load More"
              />
            </div>
          </div>
        </MainBlock>
        <SideBlock className="hidden lg:block" />
      </WrapperBlock>
    </MainContainer>
  );
}
